// prettier-ignore
import { AddEquation,  Box3, BoxGeometry, Camera, Color, CustomBlending, DoubleSide, DstAlphaFactor, InstancedMesh, MathUtils, Matrix4, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, SrcAlphaFactor, Texture, TextureLoader, Vector3, } from "three";

import { generateUUID } from 'three/src/math/MathUtils';
import { RENDER_ORDER } from '../comon';
interface PS {
  position: Vector3;
  velocity: Vector3;
  rotation: Vector3;
  maxOpacity: number;
  size: number;
  life: number;
  maxLife: number;
  rand: number;
  speedFactor: number;
}

interface PSSourceConfig {
  pos: Vector3;
  posRandomBox?: Box3;
  psNum?: number;
  reborn?: boolean;
  maxSize?: number;
  minSize?: number;
  initVel?: Vector3;
  initVelRandom?: number;
  life?: number;
  lifeRandom?: number;
  autoDispose?: boolean;
  speedFactor?: number;
  g?: Vector3;
  windStrength?: number;
  scaleByLifeBirth?: boolean;
  scaleByLifeDead?: boolean;
}
interface PSSource extends PSSourceConfig {
  psIndexArr: [];
}
const mat = new Matrix4();
const hidePSMat4 = new Matrix4().setPosition(9999, 9999, 9999);
const psTex = new TextureLoader().load(
  `${process.env.PUBLIC_URL}/textures/dust.png`
);

export class ParticleController {
  parent: Object3D = new Object3D();
  ps: PS[] = [];
  psUnused: number[] = [];
  psSources: PSSource[] = [];
  sourceMap: Map<string, PSSource> = new Map();
  color = new Color(1, 1, 1);

  psNum = 200;
  light!: InstancedMesh;
  material!: MeshBasicMaterial;

  detectDisposeTimer: any = 0;
  tex!: Texture;

  constructor(psNum?: number, color?: Color, tex = psTex) {
    this.psNum = psNum ? psNum : this.psNum;
    this.color = color ? color : this.color;
    this.tex = tex;
    this.initPS();
  }
  initPS() {
    const geo = new PlaneGeometry(0.1, 0.1, 1, 1);
    this.material = new MeshBasicMaterial({
      map: this.tex,
      color: this.color,
      transparent: true,
      side: DoubleSide,
      blending: CustomBlending,
      blendEquation: AddEquation,
      blendSrc: SrcAlphaFactor,
      blendDst: DstAlphaFactor,
      depthWrite: false,
      // depthTest: false,
      // alphaTest: 0.5,
    });
    this.light = new InstancedMesh(geo, this.material, this.psNum);

    this.light.renderOrder = RENDER_ORDER.PARTICLE;
    for (let i = 0; i < this.light.count; i++) {
      const pos = new Vector3(9999, -9999, 9999);

      const p = {
        position: pos,
        velocity: new Vector3(),
        rotation: new Vector3(),
        maxOpacity: 1,
        size: 2,
        life: MathUtils.randFloat(0, 2),
        maxLife: MathUtils.randFloat(0, 2),
        randomSpeed: {
          x: 0,
          y: 0,
          z: 0,
        },
        rand: MathUtils.randFloat(0, 1),
        speedFactor: Math.pow(MathUtils.randFloat(1, 10), 0.5),
      };
      this.ps.push(p);

      mat.identity();
      mat.setPosition(p.position);
      this.light.setMatrixAt(i, mat);

      this.psUnused.push(i);
    }
    this.light.instanceMatrix.needsUpdate = true;
    this.light.frustumCulled = false;
    this.parent.add(this.light);
    this.detectDisposeTimer = setInterval(this.detectDispose.bind(this), 1000);
  }
  detectDispose() {
    this.sourceMap.forEach((src, id) => {
      if (src && src.autoDispose && !src.reborn) {
        const psArr = src.psIndexArr;
        let needDispose = true;
        for (let j = 0; j < psArr.length; j++) {
          const psIndex = psArr[j];
          const p = this.ps[psIndex];
          if (p.life < p.maxLife) {
            needDispose = false;
            break;
          }
        }
        if (needDispose) {
          // dispose source
          this.disposeSourceById(id);
        }
      }
    });
  }

  removeALLSource() {
    this.sourceMap.forEach((value, key) => {
      this.disposeSourceById(key);
    });
  }
  disposeSourceById(id: string) {
    const src = this.sourceMap.get(id);
    if (src) {
      const index = this.psSources.indexOf(src);
      this.psSources.splice(index, 1);
      src.psIndexArr.forEach(i => {
        this.psUnused.push(i);
        this.light.setMatrixAt(i, hidePSMat4);
      });
      this.sourceMap.delete(id);
    }
  }
  anim(time: number, delta: number, cam: Camera) {
    for (let i = 0; i < this.psSources.length; i++) {
      const src = this.psSources[i];
      const psArr = src.psIndexArr;
      for (let j = 0; j < psArr.length; j++) {
        const psIndex = psArr[j];
        const p = this.ps[psIndex];

        p.life += delta;
        let lifeProgress = p.life / p.maxLife;

        const wind = new Vector3(
          Math.sin(
            (p.position.x * 2.0) / src.windStrength! +
              p.rand +
              (time * 2.0) / src.windStrength!
          ),
          Math.sin(
            (p.position.y * 2.0) / src.windStrength! +
              p.rand +
              12.424 +
              (time * 2.0) / src.windStrength!
          ),
          Math.sin(
            (p.position.z * 2.0) / src.windStrength! +
              p.rand +
              1212.424 +
              (time * 2.0) / src.windStrength!
          )
        );
        wind.multiplyVectors(
          wind,
          new Vector3().random().subScalar(0.5).multiplyScalar(0.2).addScalar(1)
        );
        wind.multiplyScalar(src.windStrength!);

        p.velocity.addScaledVector(src.g!.clone(), delta);
        p.velocity.addScaledVector(wind, delta);
        p.velocity.multiplyScalar(0.99);

        p.position.x += p.velocity.x * delta * p.speedFactor * src.speedFactor!;
        p.position.y += p.velocity.y * delta * p.speedFactor * src.speedFactor!;
        p.position.z += p.velocity.z * delta * p.speedFactor * src.speedFactor!;

        if (p.life >= p.maxLife && src.reborn) {
          this.resetParticle(psIndex, src);
        }
        if (src.scaleByLifeBirth && src.scaleByLifeDead) {
          lifeProgress = 1 - Math.abs(lifeProgress - 0.5) * 2;
        } else if (src.scaleByLifeDead) {
          lifeProgress = 1 - p.life / p.maxLife;
        } else {
          lifeProgress = p.life / p.maxLife;
        }

        let s = MathUtils.lerp(0, p.size, lifeProgress);
        s = Math.max(0, s);

        // chang size based on camera distance
        const distance = p.position.distanceTo(cam.position);
        const maxDistance = 50;
        s = MathUtils.lerp(s, s * 1.5, distance / maxDistance);

        mat.identity();
        mat.lookAt(cam.position, p.position, cam.up);
        mat.setPosition(p.position);
        mat.multiply(new Matrix4().makeScale(s, s, s));
        this.light.setMatrixAt(psIndex, mat);
      }
    }
    this.light.instanceMatrix.needsUpdate = true;
  }

  resetParticle(index: number, src: PSSource) {
    const p = this.ps[index];
    p.position.copy(src.pos);
    p.position.add(
      new Vector3(
        MathUtils.lerp(
          src.posRandomBox!.min.x,
          src.posRandomBox!.max.x,
          Math.random()
        ),
        MathUtils.lerp(
          src.posRandomBox!.min.y,
          src.posRandomBox!.max.y,
          Math.random()
        ),
        MathUtils.lerp(
          src.posRandomBox!.min.z,
          src.posRandomBox!.max.z,
          Math.random()
        )
      )
    );
    p.rotation.set(0, 0, MathUtils.degToRad(MathUtils.randFloat(0, 360)));

    p.size = MathUtils.randFloat(src.minSize!, src.maxSize!);
    p.velocity = new Vector3()
      .random()
      .subScalar(0.5)
      .normalize()
      .multiplyScalar(src.initVel!.length());
    p.velocity.lerp(src.initVel!, 1 - src.initVelRandom!);

    p.position.add(p.velocity);
    p.maxLife = src.life!;
    p.maxLife += (Math.random() * 2 - 1) * src.lifeRandom!;
    p.life = 0;
  }

  reset(scene: Object3D) {
    scene.add(this.parent);
  }

  dispose() {
    this.light.traverse(function (mesh) {
      if (mesh instanceof Mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
    });
    this.light.parent?.remove(this.light);
  }

  addSource(config: PSSourceConfig, debug = false) {
    const newSource = {
      pos: config.pos,
      posRandomBox:
        config.posRandomBox === undefined
          ? new Box3(new Vector3(), new Vector3())
          : config.posRandomBox,
      psNum: config.psNum === undefined ? 30 : config.psNum,
      reborn: config.reborn === undefined ? true : config.reborn,
      minSize: config.minSize === undefined ? 1 : config.minSize,
      maxSize: config.maxSize === undefined ? 2 : config.maxSize,
      initVel: config.initVel === undefined ? new Vector3(0) : config.initVel,
      initVelRandom:
        config.initVelRandom === undefined ? 0.5 : config.initVelRandom,
      life: config.life === undefined ? 3 : config.life,
      lifeRandom: config.lifeRandom === undefined ? 0 : config.lifeRandom,
      autoDispose: config.autoDispose === undefined ? true : config.autoDispose,
      speedFactor: config.speedFactor === undefined ? 1 : config.speedFactor,
      g: config.g === undefined ? new Vector3(0, 0.98, 0) : config.g,
      windStrength: config.windStrength === undefined ? 1 : config.windStrength,
      scaleByLifeBirth:
        config.scaleByLifeBirth === undefined ? true : config.scaleByLifeBirth,
      scaleByLifeDead:
        config.scaleByLifeDead === undefined ? true : config.scaleByLifeDead,
      psIndexArr: [],
    } as PSSource;
    for (let i = 0; i < newSource.psNum!; i++) {
      if (this.psUnused.length > 0) {
        const psIndex = this.psUnused.shift() as number;
        this.resetParticle(psIndex, newSource);
        (newSource.psIndexArr as number[]).push(psIndex!);
      } else {
        console.warn('There is no Particle for new source!');
        break;
      }
    }
    this.psSources.push(newSource as PSSource);
    const srcId = generateUUID();
    this.sourceMap.set(srcId, newSource);

    // debug
    if (debug) {
      const box = newSource.posRandomBox!;
      const x = box.max.x - box.min.x;
      const y = box.max.y - box.min.y;
      const z = box.max.z - box.min.z;
      const mesh = new Mesh(
        new BoxGeometry(x, y, z),
        new MeshBasicMaterial({
          transparent: true,
          color: 0xff0000,
          opacity: 0.4,
        })
      );
      mesh.position.copy(newSource.pos);
      this.parent.add(mesh);
    }

    return srcId;
  }
  updatePSSource(srcId: string, position?: Vector3, reborn?: boolean) {
    const src = this.sourceMap.get(srcId);
    if (src) {
      src.pos = position ? position : src?.pos;
      src.reborn = reborn ? reborn : src?.reborn;
    }
  }
}

const PSController = new ParticleController();

export function resetPS(scene: Object3D) {
  PSController.reset(scene);
}

export function setPSSourceVisible(visible: boolean) {
  PSController.material.visible = visible;
}

export function addPSSource(config: PSSourceConfig, debug = false) {
  return PSController.addSource(config, debug);
}

export function removePSSource(id: string) {
  return PSController.disposeSourceById(id);
}

export function updatePSSource(
  srcId: string,
  position?: Vector3,
  reborn?: boolean
) {
  PSController.updatePSSource(srcId, position, reborn);
}

export function animPS(t: number, delta: number, camera: Camera) {
  PSController.anim(t, delta, camera);
}
